import { z } from "zod";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { HttpError, useTranslation } from "@refinedev/core";
import { get } from "lodash";
import { Link } from "react-router-dom";
import { CheckCircleIcon, XCircleIcon } from "@heroicons/react/solid";
import { useEffect, useMemo } from "react";
import Button from "components/button";
import Dropzone from "components/dropzone";
import Input from "components/input";
import { Label } from "components/label";
import { SwitchButton } from "components/switchButton";
import { getEventLabels } from "./labels";
import {
  PickupEventDetail,
  PickupEventFormValues,
  PickupEventVariant,
} from "./types";

interface PickupEventFormProps {
  backPath: string;
  defaultData: PickupEventDetail;
  onSubmit: (data: PickupEventFormValues) => void | Promise<void>;
  readOnly?: boolean;
  variant: PickupEventVariant;
}

export const PickupEventForm = ({
  backPath,
  defaultData,
  onSubmit,
  readOnly = false,
  variant,
}: PickupEventFormProps) => {
  const { translate } = useTranslation();
  const labels = getEventLabels(variant, translate);
  const defaultValues = useMemo(
    () => ({
      ewaiver_term: defaultData?.ewaiver_term ?? "",
      logo: defaultData?.logo ?? "",
      pickup: defaultData?.pickup ?? false,
      print_stamp: defaultData?.print_stamp ?? null,
      pickup_sign_required: defaultData?.pickup_sign_required ?? false,
      map_bib_onsite: defaultData?.map_bib_onsite ?? false,
    }),
    [
      defaultData?.ewaiver_term,
      defaultData?.logo,
      defaultData?.map_bib_onsite,
      defaultData?.pickup,
      defaultData?.pickup_sign_required,
      defaultData?.print_stamp,
    ],
  );

  const formSchema = z.object({
    pickup: z.boolean(),
    logo: z.string().min(1, labels.requiredMessage),
    ewaiver_term: z.string().optional(),
    print_stamp: z.boolean().nullable(),
    pickup_sign_required: z.boolean().nullable(),
    map_bib_onsite: z.boolean().nullable(),
  });

  const {
    control,
    register,
    handleSubmit,
    reset,
    setFocus,
    formState: { errors },
  } = useForm<PickupEventFormValues, HttpError, PickupEventFormValues>({
    defaultValues,
    resolver: zodResolver(formSchema),
  });

  useEffect(() => {
    reset(defaultValues);
  }, [defaultValues, reset]);

  const submit = (values: PickupEventFormValues) => {
    if (readOnly) return;
    onSubmit(values);
  };

  const focusFirstError = (formErrors: any) => {
    const firstErrorKey = Object.keys(formErrors)[0];
    if (firstErrorKey) {
      setFocus(firstErrorKey as any);
    }
  };

  return (
    <div className={variant === "wps" ? "w-full p-[16px]" : "w-full"}>
      <form onSubmit={handleSubmit(submit, focusFirstError)}>
        <div className="flex flex-col gap-y-2 sm:gap-y-4">
          <div className="w-full box-container">
            <div className="grid gap-x-4 gap-y-4 sm:grid-cols-8 lg:gap-x-6 lg:gap-y-6">
              <div className="sm:col-span-4">
                <Label id="pickup" name={labels.eventName} />
                <p className="font-bold">{defaultData?.name}</p>
              </div>

              <div className="sm:col-span-4">
                <Label id="online_checked" name="eWaiver" />
                {defaultData?.online_checked ? (
                  <CheckCircleIcon className="size-6 text-green-500" />
                ) : (
                  <XCircleIcon className="size-6 text-gray-300" />
                )}
              </div>

              <div className="sm:col-span-4 grid grid-cols-2 xl:grid-cols-4 gap-2">
                <div className="flex-1">
                  <Label id="pickup" name={labels.eventDisplay} />
                  <Controller
                    control={control}
                    name="pickup"
                    defaultValue={true}
                    render={({ field: { onChange, value = true } }) => (
                      <SwitchButton
                        checked={value}
                        onChange={onChange}
                        disabled={readOnly}
                      />
                    )}
                  />
                  {get(errors, "pickup", "") ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(errors, "pickup.message", "")}
                      </span>
                    </p>
                  ) : null}
                </div>

                <div className="flex-1">
                  <Label id="pickup_sign_required" name={labels.signRequired} />
                  <Controller
                    control={control}
                    name="pickup_sign_required"
                    defaultValue={true}
                    render={({ field: { onChange, value = true } }) => (
                      <Input
                        type="checkbox"
                        label=""
                        id="pickup_sign_required"
                        disabled={readOnly}
                        checked={value ?? false}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          onChange(event.target.checked)
                        }
                      />
                    )}
                  />
                  {get(errors, "pickup_sign_required", "") ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(errors, "pickup_sign_required.message", "")}
                      </span>
                    </p>
                  ) : null}
                </div>

                <div className="flex-1">
                  <Label id="map_bib_onsite" name={labels.mapBibOnsite} />
                  <Controller
                    control={control}
                    name="map_bib_onsite"
                    defaultValue={true}
                    render={({ field: { onChange, value = true } }) => (
                      <Input
                        type="checkbox"
                        label=""
                        id="map_bib_onsite"
                        disabled={readOnly}
                        checked={value ?? false}
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                          onChange(event.target.checked)
                        }
                      />
                    )}
                  />
                  {get(errors, "map_bib_onsite", "") ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {get(errors, "map_bib_onsite.message", "")}
                      </span>
                    </p>
                  ) : null}
                </div>

                {defaultData?.event_type === "attractions" ? (
                  <div className="flex-1">
                    <Label id="print_stamp" name={labels.printStamp} />
                    <Controller
                      control={control}
                      name="print_stamp"
                      defaultValue={true}
                      render={({ field: { onChange, value = true } }) => (
                        <Input
                          type="checkbox"
                          label=""
                          id="print_stamp"
                          disabled={readOnly}
                          checked={value ?? false}
                          onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            onChange(event.target.checked)
                          }
                        />
                      )}
                    />
                    {get(errors, "print_stamp", "") ? (
                      <p className="error-text">
                        <span className="font-medium">
                          {get(errors, "print_stamp.message", "")}
                        </span>
                      </p>
                    ) : null}
                  </div>
                ) : null}
              </div>

              <div className="sm:col-span-4">
                <Label id="ewaiver_term" name={labels.ewaiverTerm} />
                {readOnly ? (
                  <p className="text-sm">{defaultData?.ewaiver_term}</p>
                ) : (
                  <Input
                    id="ewaiver_term"
                    type="text"
                    maxLength={50}
                    placeholder={labels.ewaiverTerm}
                    error={errors["ewaiver_term"]}
                    {...register("ewaiver_term")}
                  />
                )}
                <div className="flex justify-between flex-row-reverse">
                  {errors["ewaiver_term"] ? (
                    <p className="error-text">
                      <span className="font-medium">
                        {errors["ewaiver_term"]?.message}
                      </span>
                    </p>
                  ) : null}
                </div>
              </div>

              <div className="col-span-full">
                <div>
                  <Label id="logo" name={labels.eventBanner} required />
                  <Controller
                    control={control}
                    name="logo"
                    render={({ field: { onChange, value = "" } }) => (
                      <Dropzone
                        id="logo"
                        validator={{
                          width: 1440,
                          height: 600,
                        }}
                        onChange={(imageUrl: string) => onChange(imageUrl)}
                        value={value}
                        disabled={readOnly}
                      />
                    )}
                  />
                  <p className="mt-1 text-sm text-[#868E95]">
                    {labels.imageHint}
                  </p>
                </div>
                {get(errors, "logo", "") ? (
                  <p className="error-text">
                    <span className="font-medium">
                      {get(errors, "logo.message", "")}
                    </span>
                  </p>
                ) : null}
              </div>
            </div>
          </div>

          {!readOnly ? (
            <div className="w-full px-4 py-4 sm:px-6 bg-white sticky bottom-0 z-[2] mt-auto">
              <div className="flex items-center justify-end gap-x-4">
                <Link to={backPath}>
                  <Button
                    type="button"
                    className="w-20 bg-white text-brand-500 border border-solid border-brand-500 hover:bg-gray-50 active:bg-gray-100"
                  >
                    {labels.cancel}
                  </Button>
                </Link>
                <Button className="w-20" type="submit">
                  {labels.save}
                </Button>
              </div>
            </div>
          ) : null}
        </div>
      </form>
    </div>
  );
};
